import React, { useState, useEffect } from "react";
import apiClient from "../api/apiClient";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ProjetDetails = () => {
  const [projet, setProjet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();

  useEffect(() => {
    const fetchProjetDetails = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get(`/projets/${id}`);
        setProjet(response.data);
        setLoading(false);
      } catch (error) {
        setError("Impossible de charger les détails du projet");
        setLoading(false);
      }
    };
    fetchProjetDetails();
  }, [id]);

  if (loading) {
    return (
      <div className="container pt120 pb120 text-center">
        <div className="lds-ring">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
        <p>Chargement des détails du projet...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container pt120 pb120">
        <div className="alert alert-danger text-center">
          <h3>Erreur</h3>
          <p>{error}</p>
          <Link to="/projets" className="ree-btn ree-btn-grdt1 mt30">
            Retour aux projets <i className="fas fa-arrow-right fa-btn"></i>
          </Link>
        </div>
      </div>
    );
  }

  if (!projet) {
    return (
      <div className="container pt120 pb120">
        <div className="alert alert-warning text-center">
          <h3>Projet non trouvé</h3>
          <p>Le projet que vous recherchez n'existe pas.</p>
          <Link to="/projets" className="ree-btn ree-btn-grdt1 mt30">
            Retour aux projets <i className="fas fa-arrow-right fa-btn"></i>
          </Link>
        </div>
      </div>
    );
  }

  const technologies = Array.isArray(projet.technologies)
    ? projet.technologies
    : [];
  const projectImage = projet.image_path || "images/portfolio/default.jpg";

  // JSON-LD structured data
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: projet.titre_projet,
    description: projet.description || "",
    image: projectImage,
    url: `https://www.dvelgegroup.com/projets/${id}`,
    datePublished: projet.date_publication || projet.date_debut,
    author: {
      "@type": "Organization",
      name: "DVELGE GROUP",
    },
    keywords: technologies.map((t) => t.nom).join(", "),
  };

  return (
    <div>
      <Helmet>
        <title>{projet.titre_projet} | DVELGE GROUP</title>
        <meta
          name="description"
          content={
            projet.description || "Découvrez ce projet sur DVELGE GROUP."
          }
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={`${projet.titre_projet} | DVELGE GROUP`}
        />
        <meta property="og:description" content={projet.description || ""} />
        <meta property="og:image" content={projectImage} />
        <meta
          property="og:url"
          content={`https://www.dvelgegroup.com/projets/${id}`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${projet.titre_projet} | DVELGE GROUP`}
        />
        <meta name="twitter:description" content={projet.description || ""} />
        <meta name="twitter:image" content={projectImage} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Header Projet */}
      <div className="port-head-sec pt85 pb120 r-bg-x">
        <div className="container">
          <div className="row vcenter pt80">
            <div className="col-lg-6">
              <div className="page-headings">
                <span
                  className="sub-heading mb15"
                  data-aos="fade-up"
                  data-aos-delay="200"
                >
                  <i className="fas fa-briefcase mr5"></i> Détails du Projet
                </span>
                <h1 className="mb15" data-aos="fade-up" data-aos-delay="400">
                  {projet.titre_projet}{" "}
                  <span className="ree-text rt40">{projet.statut}</span>
                </h1>
                <p className="h-light" data-aos="fade-up" data-aos-delay="600">
                  {projet.description ||
                    "Aucune description disponible pour ce projet."}
                </p>
                <div className="mt30" data-aos="fade-up" data-aos-delay="800">
                  <Link to="/projets" className="ree-btn ree-btn-grdt1 mw-80">
                    <i className="fas fa-arrow-left fa-btn mr5"></i> Tous les
                    projets
                  </Link>
                  {projet.lien_demo && (
                    <a
                      href={projet.lien_demo}
                      className="ree-btn-outline mw-80 ml20"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Voir la démo{" "}
                      <i className="fas fa-external-link-alt fa-btn"></i>
                    </a>
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div
                className="sol-img m-mt30"
                data-aos="fade-in"
                data-aos-delay="400"
              >
                <img
                  src={projectImage}
                  alt={projet.titre_projet}
                  className="img-fluid"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* About & Details */}
      <section className="r-bg-a sec-pad">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <div className="details-port">
                <h2 className="mb15">À propos du projet</h2>
                <div
                  dangerouslySetInnerHTML={{
                    __html:
                      projet.description ||
                      "Aucune description détaillée disponible pour ce projet.",
                  }}
                />
                {projet.objectifs && (
                  <div className="mt30">
                    <h3 className="mb15">Objectifs</h3>
                    <div
                      dangerouslySetInnerHTML={{ __html: projet.objectifs }}
                    />
                  </div>
                )}
                {projet.resultats && (
                  <div className="mt30">
                    <h3 className="mb15">Résultats</h3>
                    <div
                      dangerouslySetInnerHTML={{ __html: projet.resultats }}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="col-lg-6">
              <div className="technolo-sec">
                <h3 className="mb25">Technologies utilisées</h3>
                {technologies.length > 0 ? (
                  technologies.map((tech, index) => (
                    <div className="icon-with-title" key={index}>
                      <a href="javascript:void(0)">
                        <div className="iwt-icon">
                          <img
                            src={
                              tech.icon_path ||
                              `images/icons/${tech.nom.toLowerCase()}.svg`
                            }
                            alt={tech.nom}
                            onError={(e) => {
                              e.target.src = "images/icons/default-tech.svg";
                            }}
                          />
                        </div>
                        <div className="iwt-content">
                          <p>{tech.nom}</p>
                        </div>
                      </a>
                    </div>
                  ))
                ) : (
                  <p>Aucune technologie spécifiée pour ce projet.</p>
                )}
              </div>
              {projet.date_debut && (
                <div className="project-metadata mt30">
                  <h3 className="mb15">Informations complémentaires</h3>
                  <ul className="project-meta-list">
                    {projet.date_debut && (
                      <li>
                        <strong>Date de début:</strong>{" "}
                        {new Date(projet.date_debut).toLocaleDateString()}
                      </li>
                    )}
                    {projet.date_fin && (
                      <li>
                        <strong>Date de fin:</strong>{" "}
                        {new Date(projet.date_fin).toLocaleDateString()}
                      </li>
                    )}
                    {projet.client && (
                      <li>
                        <strong>Client:</strong> {projet.client}
                      </li>
                    )}
                    {projet.categorie && (
                      <li>
                        <strong>Catégorie:</strong> {projet.categorie}
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Galerie */}
          {projet.galerie_images && projet.galerie_images.length > 0 && (
            <div className="row mt60">
              <div className="col-12">
                <h2 className="mb30">Galerie du projet</h2>
              </div>
              {projet.galerie_images.map((image, index) => (
                <div className="col-lg-4 col-md-6 mt30" key={index}>
                  <div className="project-image-box">
                    <img
                      src={image.url}
                      alt={image.alt || `Image ${index + 1}`}
                      className="img-fluid"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="row mt60">
            <div className="col-12 text-center">
              <h3 className="mb30">Vous avez un projet similaire ?</h3>
              <Link to="/contacts" className="ree-btn ree-btn-grdt1">
                Discutons-en <i className="fas fa-arrow-right fa-btn"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjetDetails;
